import { parseDatenFile } from '@emdzej/ncsx-daten';
import { parseAt, parseAtM00, parseAtZus, parseSgfam, parseZst, } from '@emdzej/ncsx-text-tables';
import { loadBrRef, resolveChassisCode } from './br-ref.js';
import { CabdLoader } from './cabd-loader.js';
import { indexAt, indexSgfam, indexZcsut, indexZst } from './indexes.js';
import { loadSwtFile } from './swt.js';
const decodeLatin1 = (bytes) => {
    let s = '';
    for (let i = 0; i < bytes.length; i++)
        s += String.fromCharCode(bytes[i]);
    return s;
};
const layoutFor = (chassis) => {
    const lower = chassis.toLowerCase();
    const upper = chassis.toUpperCase();
    return {
        dir: lower,
        dst: `${lower}/${upper}DST.000`,
        sget: `${lower}/${upper}SGET.000`,
        sgvt: `${lower}/${upper}SGVT.000`,
        zcsut: `${lower}/${upper}ZCSUT.000`,
        cvt: `${lower}/${upper}CVT.000`,
        sgfam: `${lower}/${upper}SGFAM.DAT`,
        zst: `${lower}/${upper}ZST.000`,
        at: `${lower}/${upper}AT.000`,
        atM00: `${lower}/${upper}AT.M00`,
        atZus: `${lower}/${upper}AT.ZUS`,
    };
};
async function readOptionalDaten(source, path, onWarning) {
    if (!(await source.exists(path))) {
        onWarning({ kind: 'missing-optional', file: path, message: 'file not found' });
        return undefined;
    }
    try {
        return parseDatenFile(await source.read(path));
    }
    catch (err) {
        onWarning({
            kind: 'parse-failure',
            file: path,
            message: err instanceof Error ? err.message : String(err),
        });
        return undefined;
    }
}
async function readOptionalText(source, path, parser, onWarning) {
    if (!(await source.exists(path))) {
        onWarning({ kind: 'missing-optional', file: path, message: 'file not found' });
        return undefined;
    }
    try {
        return parser(decodeLatin1(await source.read(path)));
    }
    catch (err) {
        onWarning({
            kind: 'parse-failure',
            file: path,
            message: err instanceof Error ? err.message : String(err),
        });
        return undefined;
    }
}
/**
 * Load a complete chassis bundle from a {@link ChassisSource}.
 *
 * - `BR_REF.DAT` is opened (and aliasing applied) to canonicalise the chassis code.
 * - The five chassis binary tables (DST/SGET/SGVT/ZCSUT/CVT) are read in parallel.
 * - The text companions (SGFAM/ZST/AT/AT.M00/AT.ZUS) are read in parallel.
 * - CABD `.Cxx` files are **not** preloaded; use `chassis.cabd.listModules()` to
 *   enumerate them and `chassis.cabd.openModule(basename, ci)` to load one. See
 *   `docs/ecu-selection.md` §8 for how to derive `(basename, ci)` from a SelectedSg.
 *
 * Missing optional files (anything except `DST.000`) emit a warning and leave the matching
 * field `undefined`. Missing `BR_REF.DAT` or `DST.000` throw.
 */
export async function loadChassis(source, requestedCode, options = {}) {
    const onWarning = options.onWarning ?? (() => { });
    const brRef = await loadBrRef(source);
    const code = resolveChassisCode(brRef, requestedCode);
    const layout = layoutFor(code);
    if (!(await source.exists(layout.dst))) {
        throw new Error(`chassis ${code}: ${layout.dst} not found`);
    }
    const [dstBytes, sget, sgvt, zcsut, cvt] = await Promise.all([
        source.read(layout.dst),
        readOptionalDaten(source, layout.sget, onWarning),
        readOptionalDaten(source, layout.sgvt, onWarning),
        readOptionalDaten(source, layout.zcsut, onWarning),
        readOptionalDaten(source, layout.cvt, onWarning),
    ]);
    const dst = parseDatenFile(dstBytes);
    const [sgfamFile, zstFile, atFile, atM00File, atZusFile] = await Promise.all([
        readOptionalText(source, layout.sgfam, parseSgfam, onWarning),
        readOptionalText(source, layout.zst, parseZst, onWarning),
        readOptionalText(source, layout.at, parseAt, onWarning),
        readOptionalText(source, layout.atM00, parseAtM00, onWarning),
        readOptionalText(source, layout.atZus, parseAtZus, onWarning),
    ]);
    const sgfam = sgfamFile ? indexSgfam(sgfamFile.rows) : new Map();
    const zst = zstFile ? indexZst(zstFile) : undefined;
    const atIndex = atFile ? indexAt(atFile.records) : undefined;
    const atRaw = atFile;
    const atZus = atZusFile;
    // SWT lookup tables — chassis ships them as `SWT(ASW|FSW|PSW)<NN>.dat`. The `<NN>` is a
    // revision suffix; we don't know it upfront, so `loadSwtFile` lists the directory and picks
    // the matching file. Missing files emit a warning and leave the field `undefined`.
    const [swtAsw, swtFsw, swtPsw] = await Promise.all([
        loadSwtFile(source, layout.dir, 'ASW').catch((err) => {
            onWarning({ kind: 'parse-failure', file: `${layout.dir}/SWTASW*.dat`, message: String(err) });
            return undefined;
        }),
        loadSwtFile(source, layout.dir, 'FSW').catch((err) => {
            onWarning({ kind: 'parse-failure', file: `${layout.dir}/SWTFSW*.dat`, message: String(err) });
            return undefined;
        }),
        loadSwtFile(source, layout.dir, 'PSW').catch((err) => {
            onWarning({ kind: 'parse-failure', file: `${layout.dir}/SWTPSW*.dat`, message: String(err) });
            return undefined;
        }),
    ]);
    if (!swtAsw)
        onWarning({ kind: 'missing-optional', file: `${layout.dir}/SWTASW*.dat`, message: 'no SWTASW lookup' });
    if (!swtFsw)
        onWarning({ kind: 'missing-optional', file: `${layout.dir}/SWTFSW*.dat`, message: 'no SWTFSW lookup' });
    if (!swtPsw)
        onWarning({ kind: 'missing-optional', file: `${layout.dir}/SWTPSW*.dat`, message: 'no SWTPSW lookup' });
    const cabd = new CabdLoader(source, layout.dir);
    return {
        code,
        requestedCode,
        dir: layout.dir,
        brRef,
        dst,
        sget: sget ?? emptyDaten(),
        sgvt: sgvt ?? emptyDaten(),
        cvt: cvt ?? emptyDaten(),
        // FA-master chassis don't ship ZCSUT — leave the field undefined so
        // the ZCS editor can fall back to free-text inputs.
        zcsut: zcsut ? indexZcsut(zcsut) : undefined,
        sgfam,
        zst,
        at: atIndex,
        atRaw,
        atM00: atM00File,
        atZus,
        swtAsw,
        swtFsw,
        swtPsw,
        cabd,
    };
}
const emptyDaten = () => ({ signatures: [], blocks: [], rowsInOrder: [] });
//# sourceMappingURL=loader.js.map